/**
 * UserStatsPanel Component
 *
 * Display aggregate user statistics
 * Feature: 045-admin-user-tool
 */

import type { AdminUserStats } from '../../graphql/hooks/useAdminUsersGraphQL';

// Re-export AdminUserStats as UserStatsResponse for compatibility
type UserStatsResponse = AdminUserStats;

interface UserStatsPanelProps {
  stats: UserStatsResponse;
  loading?: boolean;
}

export function UserStatsPanel({ stats, loading }: UserStatsPanelProps) {
  const handleExportCSV = () => {
    const csvContent = [
      ['Metric', 'Value'],
      ['Total Users', stats.total],
      ['Verified Users', stats.verified],
      ['Unverified Users', stats.unverified],
      ['Test Users', stats.testUsers],
      ['Active (7 days)', stats.active7d],
      ['Active (30 days)', stats.active30d],
      ['Active (90 days)', stats.active90d],
      ['Inactive Users', stats.inactive],
      ['Average Photos per User', stats.avgPhotos?.toFixed(2) || '0.00'],
      ['Average Collections per User', stats.avgCollections?.toFixed(2) || '0.00']
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `user-stats-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-surface-2 rounded w-1/4"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-surface-2 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const verifiedPercentage = stats.total > 0
    ? Math.round((stats.verified / stats.total) * 100)
    : 0;

  return (
    <div className="bg-surface-1 rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">User Statistics</h2>
        <button
          onClick={handleExportCSV}
          className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-surface-2 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a 3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Total Users"
          value={stats.total.toLocaleString()}
          subtext={`${verifiedPercentage}% verified`}
        />
        <StatCard
          label="Verified"
          value={stats.verified.toLocaleString()}
          valueColor="text-green-500"
        />
        <StatCard
          label="Unverified"
          value={stats.unverified.toLocaleString()}
          valueColor="text-yellow-500"
        />
        <StatCard
          label="Test Users"
          value={stats.testUsers.toLocaleString()}
          valueColor="text-purple-500"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Active (7d)"
          value={stats.active7d.toLocaleString()}
        />
        <StatCard
          label="Active (30d)"
          value={stats.active30d.toLocaleString()}
        />
        <StatCard
          label="Active (90d)"
          value={stats.active90d.toLocaleString()}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <StatCard
          label="Avg Photos"
          value={stats.avgPhotos?.toFixed(1) || '0.0'}
        />
        <StatCard
          label="Avg Collections"
          value={stats.avgCollections?.toFixed(1) || '0.0'}
        />
      </div>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  subtext?: string;
  valueColor?: string;
}

function StatCard({ label, value, subtext, valueColor = 'text-foreground' }: StatCardProps) {
  return (
    <div className="bg-surface-2 rounded-lg p-4 border border-border">
      <div className="text-sm text-muted mb-1">{label}</div>
      <div className={`text-2xl font-bold ${valueColor}`}>{value}</div>
      {subtext && <div className="text-xs text-muted mt-1">{subtext}</div>}
    </div>
  );
}
